/**
 * Session-results import — the pure core behind the Record app
 * (src/apps/session-import, which re-exports it) and the public API
 * (POST /v1/import/session-results). No imports, so the Vite app and the Deno
 * edge function run the same code: one parser, one item matcher, one
 * participant matcher.
 *
 * Turns column-mapped spreadsheet rows into a normalized session result in
 * one of two shapes (PRD-session-results-import §4):
 *   - ranked-list:  one row per item (title, rank?, score?)
 *   - full-results: one row per score (item × participant × criterion)
 * Shape is inferred from which fields carry values, never asked up front.
 *
 * Doctrine: clarify rather than guess — anything ambiguous is a blocker with
 * the row numbers named, surfaced before anything is written.
 */

export type ImportShape = 'ranked-list' | 'full-results';

export type RankingSource = 'explicit-rank' | 'score' | 'computed-scores' | 'file-order';

export interface ImportedItem {
  /** Stable identity within the file: item_id, else external_id, else normalized title. */
  key: string;
  title: string;
  description?: string;
  externalId?: string;
  /** Platform UUID supplied by the file (resolution verifies it). */
  itemId?: string;
  /** Final derived rank, dense 1..n. */
  rank: number;
  /** Aggregate score (explicit for ranked-list, computed for full-results). */
  score: number | null;
}

export interface ImportedCriterion {
  name: string;
  weight: number;
}

export interface ImportedResponse {
  participant: string;
  /** The participant's email from the file, when it has one — the only way they match a member. */
  email?: string;
  /** itemKey → criterion name → score. */
  scores: Record<string, Record<string, number>>;
}

export interface ParsedSessionImport {
  shape: ImportShape;
  /** Ordered by final rank. */
  items: ImportedItem[];
  /** Empty for ranked-list. */
  criteria: ImportedCriterion[];
  participants: string[];
  responses: ImportedResponse[];
  rankingSource: RankingSource;
  /** Non-blocking derivation notes, shown on the confirm step. */
  notes: string[];
  /** Must be empty to proceed. */
  blockers: string[];
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanString(value: unknown): string {
  return String(value ?? '').trim();
}

function parseNumber(value: unknown): number | null {
  const text = cleanString(value).replace(/,/g, '');
  if (!text) return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : null;
}

function normalizeKey(text: string): string {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
}

interface CleanRow {
  /** 1-based row number in the file (after the header). */
  rowNumber: number;
  title: string;
  description: string;
  externalId: string;
  itemId: string;
  rank: number | null;
  score: number | null;
  participant: string;
  participantEmail: string;
  criterion: string;
  weight: number | null;
}

function toCleanRows(rows: Record<string, unknown>[]): CleanRow[] {
  return rows
    .map((row, index) => ({
      rowNumber: index + 1,
      title: cleanString(row.title),
      description: cleanString(row.description),
      externalId: cleanString(row.external_id),
      itemId: cleanString(row.item_id),
      rank: parseNumber(row.rank),
      score: parseNumber(row.score),
      participant: cleanString(row.participant),
      participantEmail: cleanString(row.participant_email),
      criterion: cleanString(row.criterion),
      weight: parseNumber(row.weight),
    }))
    .filter(
      (r) =>
        r.title || r.externalId || r.itemId || r.participant || r.participantEmail || r.criterion ||
        r.rank !== null || r.score !== null
    );
}

function itemKeyFor(row: CleanRow): string {
  if (row.itemId) return `id:${row.itemId.toLowerCase()}`;
  if (row.externalId) return `ext:${row.externalId.toLowerCase()}`;
  return `title:${normalizeKey(row.title)}`;
}

function rankItems(
  items: ImportedItem[],
  hasExplicitRanks: boolean,
  explicitRankByKey: Map<string, number>,
  notes: string[]
): { ordered: ImportedItem[]; rankingSource: RankingSource } {
  let rankingSource: RankingSource;
  let ordered: ImportedItem[];
  if (hasExplicitRanks) {
    rankingSource = 'explicit-rank';
    ordered = [...items].sort((a, b) => {
      const ra = explicitRankByKey.get(a.key) ?? Number.MAX_SAFE_INTEGER;
      const rb = explicitRankByKey.get(b.key) ?? Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
    const missing = items.filter((i) => !explicitRankByKey.has(i.key));
    if (missing.length) {
      notes.push(
        `${missing.length} item${missing.length === 1 ? '' : 's'} had no rank value and sort after the ranked ones.`
      );
    }
  } else if (items.some((i) => i.score !== null)) {
    rankingSource = 'score';
    ordered = [...items].sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
  } else {
    rankingSource = 'file-order';
    ordered = [...items];
  }
  // Dense re-rank 1..n; ties in the source keep file order (stable sort).
  ordered.forEach((item, index) => {
    item.rank = index + 1;
  });
  return { ordered, rankingSource };
}

function parseRankedList(rows: CleanRow[], notes: string[], blockers: string[]): ParsedSessionImport {
  const byKey = new Map<string, ImportedItem>();
  const explicitRankByKey = new Map<string, number>();
  const duplicateRows: number[] = [];
  const untitledRows: number[] = [];

  for (const row of rows) {
    if (!row.title && !row.itemId && !row.externalId) {
      untitledRows.push(row.rowNumber);
      continue;
    }
    const key = itemKeyFor(row);
    if (byKey.has(key)) {
      duplicateRows.push(row.rowNumber);
      continue;
    }
    if (row.itemId && !UUID_PATTERN.test(row.itemId)) {
      blockers.push(`Row ${row.rowNumber}: "${row.itemId}" is not a valid platform item ID.`);
      continue;
    }
    byKey.set(key, {
      key,
      title: row.title || row.externalId || row.itemId,
      description: row.description || undefined,
      externalId: row.externalId || undefined,
      itemId: row.itemId || undefined,
      rank: 0,
      score: row.score,
    });
    if (row.rank !== null) explicitRankByKey.set(key, row.rank);
  }

  if (untitledRows.length) {
    blockers.push(`Rows without an item title: ${untitledRows.join(', ')}.`);
  }
  if (duplicateRows.length) {
    blockers.push(
      `Duplicate items in the file (same title or ID) at rows ${duplicateRows.join(', ')} — a ranked list needs each item once.`
    );
  }
  if (byKey.size < 2) {
    blockers.push('At least two items are needed to import a session result.');
  }

  const { ordered, rankingSource } = rankItems(
    [...byKey.values()],
    explicitRankByKey.size > 0,
    explicitRankByKey,
    notes
  );

  if (rankingSource === 'file-order') {
    notes.push('No rank or score column mapped — the file’s row order is the ranking.');
  } else if (rankingSource === 'score' && explicitRankByKey.size === 0) {
    notes.push('Ranking derived from the score column, highest first.');
  }

  return {
    shape: 'ranked-list',
    items: ordered,
    criteria: [],
    participants: [],
    responses: [],
    rankingSource,
    notes,
    blockers,
  };
}

const DEFAULT_CRITERION = 'Overall';
const DEFAULT_PARTICIPANT = 'Group';

function parseFullResults(rows: CleanRow[], notes: string[], blockers: string[]): ParsedSessionImport {
  const itemsByKey = new Map<string, ImportedItem>();
  const explicitRankByKey = new Map<string, number>();
  const rankConflicts = new Set<string>();
  const criteriaByName = new Map<string, ImportedCriterion>();
  const weightConflicts = new Set<string>();
  const responsesByParticipant = new Map<string, ImportedResponse>();
  const duplicateCells: string[] = [];
  const untitledRows: number[] = [];
  const scorelessRows: number[] = [];
  const badEmailRows: number[] = [];
  const emailConflicts = new Set<string>();

  let usedDefaultCriterion = false;
  let usedDefaultParticipant = false;

  for (const row of rows) {
    if (!row.title && !row.itemId && !row.externalId) {
      untitledRows.push(row.rowNumber);
      continue;
    }
    if (row.itemId && !UUID_PATTERN.test(row.itemId)) {
      blockers.push(`Row ${row.rowNumber}: "${row.itemId}" is not a valid platform item ID.`);
      continue;
    }
    if (row.score === null) {
      scorelessRows.push(row.rowNumber);
      continue;
    }

    const key = itemKeyFor(row);
    let item = itemsByKey.get(key);
    if (!item) {
      item = {
        key,
        title: row.title || row.externalId || row.itemId,
        description: row.description || undefined,
        externalId: row.externalId || undefined,
        itemId: row.itemId || undefined,
        rank: 0,
        score: null,
      };
      itemsByKey.set(key, item);
    } else if (!item.description && row.description) {
      item.description = row.description;
    }

    if (row.rank !== null) {
      const prior = explicitRankByKey.get(key);
      if (prior !== undefined && prior !== row.rank) {
        rankConflicts.add(item.title);
      } else {
        explicitRankByKey.set(key, row.rank);
      }
    }

    const criterion = row.criterion || DEFAULT_CRITERION;
    if (!row.criterion) usedDefaultCriterion = true;
    const existingCriterion = criteriaByName.get(criterion);
    if (!existingCriterion) {
      criteriaByName.set(criterion, { name: criterion, weight: row.weight ?? 1 });
    } else if (row.weight !== null && existingCriterion.weight !== row.weight) {
      weightConflicts.add(criterion);
    }

    // A row with only an email names its participant by that email.
    const participant = row.participant || row.participantEmail || DEFAULT_PARTICIPANT;
    if (!row.participant && !row.participantEmail) usedDefaultParticipant = true;
    if (row.participantEmail && !EMAIL_PATTERN.test(row.participantEmail)) {
      badEmailRows.push(row.rowNumber);
      continue;
    }
    let response = responsesByParticipant.get(participant);
    if (!response) {
      response = { participant, scores: {} };
      responsesByParticipant.set(participant, response);
    }
    if (row.participantEmail) {
      if (!response.email) response.email = row.participantEmail;
      else if (normalizeEmail(response.email) !== normalizeEmail(row.participantEmail)) emailConflicts.add(participant);
    }
    const itemScores = (response.scores[key] ??= {});
    if (criterion in itemScores) {
      duplicateCells.push(`row ${row.rowNumber} (${participant} × ${item.title} × ${criterion})`);
      continue;
    }
    itemScores[criterion] = row.score;
  }

  if (untitledRows.length) {
    blockers.push(`Rows without an item title: ${untitledRows.join(', ')}.`);
  }
  if (scorelessRows.length) {
    blockers.push(`Rows without a numeric score: ${scorelessRows.join(', ')} — every full-results row needs one.`);
  }
  if (badEmailRows.length) {
    blockers.push(`Rows whose participant email is not an email address: ${badEmailRows.join(', ')}.`);
  }
  if (emailConflicts.size) {
    blockers.push(
      `Two different emails for the same participant: ${[...emailConflicts].join(', ')} — a participant’s email must be the same on every row.`
    );
  }
  if (duplicateCells.length) {
    blockers.push(`The same participant scored the same item on the same criterion twice: ${duplicateCells.join('; ')}.`);
  }
  if (rankConflicts.size) {
    blockers.push(
      `Conflicting rank values for: ${[...rankConflicts].join(', ')} — an item’s rank must be the same on every row.`
    );
  }
  if (itemsByKey.size < 2) {
    blockers.push('At least two items are needed to import a session result.');
  }

  if (weightConflicts.size) {
    notes.push(
      `Conflicting weights for ${[...weightConflicts].join(', ')} — the first value in the file was used.`
    );
  }
  if (usedDefaultCriterion) {
    notes.push(`Rows without a criterion were scored under "${DEFAULT_CRITERION}".`);
  }
  if (usedDefaultParticipant) {
    notes.push(`Rows without a participant were recorded as "${DEFAULT_PARTICIPANT}".`);
  }

  const criteria = [...criteriaByName.values()];

  // Aggregate: weighted mean of criterion means (matching get_group_results math).
  for (const item of itemsByKey.values()) {
    let weightedSum = 0;
    let weightTotal = 0;
    for (const criterion of criteria) {
      const scores: number[] = [];
      for (const response of responsesByParticipant.values()) {
        const value = response.scores[item.key]?.[criterion.name];
        if (value !== undefined) scores.push(value);
      }
      if (scores.length === 0) continue;
      const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
      weightedSum += criterion.weight * mean;
      weightTotal += criterion.weight;
    }
    item.score = weightTotal > 0 ? Number((weightedSum / weightTotal).toFixed(4)) : null;
  }

  const hasExplicitRanks = explicitRankByKey.size > 0;
  const { ordered } = rankItems(
    [...itemsByKey.values()],
    hasExplicitRanks,
    explicitRankByKey,
    notes
  );

  if (hasExplicitRanks) {
    notes.push('Ranking taken from the file’s rank column; weighted scores computed for context.');
  } else {
    notes.push('Ranking computed from the scores: weighted mean of each criterion’s average, highest first.');
  }

  return {
    shape: 'full-results',
    items: ordered,
    criteria,
    participants: [...responsesByParticipant.keys()],
    responses: [...responsesByParticipant.values()],
    rankingSource: hasExplicitRanks ? 'explicit-rank' : 'computed-scores',
    notes,
    blockers,
  };
}

/**
 * Parse column-mapped rows into a normalized session result.
 * Shape is inferred: any participant, participant email or criterion value → full-results.
 */
export function parseSessionImport(rows: Record<string, unknown>[]): ParsedSessionImport {
  const notes: string[] = [];
  const blockers: string[] = [];
  const clean = toCleanRows(rows);

  if (clean.length === 0) {
    blockers.push('No usable rows found — check the mapping and that the sheet has data.');
    return {
      shape: 'ranked-list',
      items: [],
      criteria: [],
      participants: [],
      responses: [],
      rankingSource: 'file-order',
      notes,
      blockers,
    };
  }

  const isFullResults = clean.some((r) => r.participant || r.participantEmail || r.criterion);
  return isFullResults
    ? parseFullResults(clean, notes, blockers)
    : parseRankedList(clean, notes, blockers);
}

/**
 * A participant's personal weighted score for one item (full-results shape) —
 * the same weighted-mean math as the aggregate, over their own scores only.
 */
export function participantItemScore(
  response: ImportedResponse,
  itemKey: string,
  criteria: ImportedCriterion[]
): number | null {
  const itemScores = response.scores[itemKey];
  if (!itemScores) return null;
  let weightedSum = 0;
  let weightTotal = 0;
  for (const criterion of criteria) {
    const value = itemScores[criterion.name];
    if (value === undefined) continue;
    weightedSum += criterion.weight * value;
    weightTotal += criterion.weight;
  }
  return weightTotal > 0 ? Number((weightedSum / weightTotal).toFixed(4)) : null;
}

// ── Matching: items to the pool, participants to members ─────────────────────

export type MatchReason = 'id' | 'external-id' | 'title';

export interface ItemResolution {
  key: string;
  title: string;
  description?: string;
  externalId?: string;
  /** What will happen on commit; the user can override either way. */
  action: 'link' | 'create';
  /** The existing item this row can link to (when a match was found). */
  matchItemId?: string;
  matchTitle?: string;
  matchReason?: MatchReason;
  /** Set when a match was claimed but could not be honored (shown to the user). */
  note?: string;
}

/** An active item already in the workspace, as matching needs it. */
export interface PoolItem {
  id: string;
  title: string | null;
  external_id?: string | null;
}

/**
 * Deterministic resolution (never AI): id → external_id → case-insensitive
 * exact title. Ambiguity defaults to Create with a note — the post-commit
 * sweep is where fuzzy judgment happens. `pool` is the workspace's active items.
 */
export function resolveItemMatches(items: ImportedItem[], pool: PoolItem[]): ItemResolution[] {
  const byId = new Map(pool.map((i) => [i.id, i]));
  const byExternalId = new Map<string, PoolItem[]>();
  const byTitle = new Map<string, PoolItem[]>();
  for (const item of pool) {
    const ext = (item.external_id ?? '').trim().toLowerCase();
    if (ext) (byExternalId.get(ext) ?? byExternalId.set(ext, []).get(ext)!).push(item);
    const title = normalizeKey(item.title ?? '');
    if (title) (byTitle.get(title) ?? byTitle.set(title, []).get(title)!).push(item);
  }

  return items.map((item): ItemResolution => {
    const base = {
      key: item.key,
      title: item.title,
      description: item.description,
      externalId: item.externalId,
    };
    const link = (match: PoolItem, matchReason: MatchReason): ItemResolution => ({
      ...base,
      action: 'link',
      matchItemId: match.id,
      matchTitle: match.title ?? '',
      matchReason,
    });

    if (item.itemId) {
      const match = byId.get(item.itemId);
      if (match) return link(match, 'id');
      return { ...base, action: 'create', note: 'The platform item ID in the file was not found — a new item will be created.' };
    }

    if (item.externalId) {
      const matches = byExternalId.get(item.externalId.toLowerCase()) ?? [];
      if (matches.length === 1) return link(matches[0], 'external-id');
      if (matches.length > 1) {
        return { ...base, action: 'create', note: 'Several existing items share this external ID — creating a new item; the sweep will flag duplicates.' };
      }
    }

    const titleMatches = byTitle.get(normalizeKey(item.title)) ?? [];
    if (titleMatches.length === 1) return link(titleMatches[0], 'title');
    if (titleMatches.length > 1) {
      return { ...base, action: 'create', note: 'Several existing items share this title — creating a new item; the sweep will flag duplicates.' };
    }

    return { ...base, action: 'create' };
  });
}

/** An active workspace member and the email on their account. */
export interface MemberEmail {
  userId: string;
  email: string;
}

export interface ParticipantMatch {
  participant: string;
  email?: string;
  /** The member whose account carries this participant's email; null when none does. */
  userId: string | null;
}

/** Email addresses compare without surrounding spaces or letter case (accounts store them lower-case). */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Imported people match members by exact email only (owner ruling Oct 28,
 * roadmap S-12) — never by name. A participant with no email, or an email no
 * member's account carries, stays recorded by name. An email two accounts
 * share matches neither.
 */
export function matchParticipantsByEmail(
  responses: ImportedResponse[],
  members: MemberEmail[]
): ParticipantMatch[] {
  const byEmail = new Map<string, string | null>();
  for (const member of members) {
    const email = normalizeEmail(member.email ?? '');
    if (!email) continue;
    const prior = byEmail.get(email);
    byEmail.set(email, prior === undefined || prior === member.userId ? member.userId : null);
  }
  return responses.map((response) => ({
    participant: response.participant,
    ...(response.email ? { email: response.email } : {}),
    userId: response.email ? byEmail.get(normalizeEmail(response.email)) ?? null : null,
  }));
}

// ── Templates ────────────────────────────────────────────────────────────────

function toCsv(rows: string[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => (/[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell))
        .join(',')
    )
    .join('\n');
}

/** Shape A template: one row per item. */
export function buildRankedListTemplateCsv(): string {
  return toCsv([
    ['title', 'rank', 'score', 'external_id', 'description'],
    ['Self-serve onboarding', '1', '8.5', 'JIRA-101', 'Reduce time-to-value for new signups'],
    ['Partner API', '2', '7.2', 'JIRA-114', ''],
    ['EU data residency', '3', '5.9', '', 'Required for two enterprise prospects'],
  ]);
}

/** Shape B template: one row per score (item × participant × criterion). */
export function buildFullResultsTemplateCsv(): string {
  return toCsv([
    ['title', 'participant', 'participant_email', 'criterion', 'score', 'weight', 'rank'],
    ['Self-serve onboarding', 'Alice', 'alice@example.com', 'Impact', '5', '2', ''],
    ['Self-serve onboarding', 'Alice', 'alice@example.com', 'Feasibility', '3', '1', ''],
    ['Self-serve onboarding', 'Ben', 'ben@example.com', 'Impact', '4', '2', ''],
    ['Self-serve onboarding', 'Ben', 'ben@example.com', 'Feasibility', '4', '1', ''],
    ['Partner API', 'Alice', 'alice@example.com', 'Impact', '3', '2', ''],
    ['Partner API', 'Alice', 'alice@example.com', 'Feasibility', '5', '1', ''],
    ['Partner API', 'Ben', 'ben@example.com', 'Impact', '4', '2', ''],
    ['Partner API', 'Ben', 'ben@example.com', 'Feasibility', '2', '1', ''],
  ]);
}
